import React, { useState, useEffect, useRef } from "react";
import "./index.scss";

import { useNavigate, useParams } from "react-router-dom";
import RealisationService from "../../../services/realisation/realisation.service";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import { showErrorToaster } from "../../../components/toaster/error";
import { showSuccessToaster } from "../../../components/toaster/success";

const realisationService = new RealisationService();

const RealisationForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  const [formData, setFormData] = useState({
    title: "",
    description: "",
    date: "",
    image: "",
    slug: "",
  });

  const [imagePreview, setImagePreview] = useState(null);
  const [error, setError] = useState("");
  const quillRef = useRef(null);
  const [tableRows, setTableRows] = useState(2);
  const [tableColumns, setTableColumns] = useState(2);

  const getQuillEditor = () => quillRef.current?.getEditor();

  const syncEditorHtml = (quillEditor) => {
    handleDescriptionChange(quillEditor.root.innerHTML);
  };

  const getSelectedTableContext = (quillEditor) => {
    const range = quillEditor.getSelection(true);
    if (!range) return null;

    const [leaf] = quillEditor.getLeaf(range.index);
    let currentNode = leaf?.domNode;
    let selectedCell = null;

    while (currentNode && currentNode !== quillEditor.root) {
      if (
        currentNode.tagName === "TD" ||
        currentNode.tagName === "TH"
      ) {
        selectedCell = currentNode;
        break;
      }
      currentNode = currentNode.parentElement;
    }

    const table = selectedCell
      ? selectedCell.closest("table")
      : quillEditor.root.querySelector("table:last-of-type");

    if (!table) return null;

    return { table, selectedCell };
  };

  const buildTableTemplate = (rows, columns) => {
    let tableBody = "";

    for (let rowIndex = 0; rowIndex < rows; rowIndex += 1) {
      const cells = [];
      for (let colIndex = 0; colIndex < columns; colIndex += 1) {
        cells.push(`<td style="padding:8px;">L${rowIndex + 1}C${colIndex + 1}</td>`);
      }
      tableBody += `<tr>${cells.join("")}</tr>`;
    }

    return `
      <table style="width:100%; border-collapse:collapse;" border="1">
        <tbody>${tableBody}</tbody>
      </table>
      <p><br/></p>
    `;
  };

  const handleInsertTable = () => {
    const quillEditor = getQuillEditor();
    if (!quillEditor) return;

    const safeRows = Math.max(1, Number.parseInt(tableRows, 10) || 1);
    const safeColumns = Math.max(1, Number.parseInt(tableColumns, 10) || 1);

    const selection = quillEditor.getSelection(true);
    const insertIndex = selection ? selection.index : quillEditor.getLength();
    const tableTemplate = buildTableTemplate(safeRows, safeColumns);

    quillEditor.clipboard.dangerouslyPasteHTML(insertIndex, tableTemplate, "user");
    quillEditor.setSelection(insertIndex + 1, 0);
    syncEditorHtml(quillEditor);
  };

  const handleAddRow = () => {
    const quillEditor = getQuillEditor();
    if (!quillEditor) return;

    const context = getSelectedTableContext(quillEditor);
    if (!context) {
      showErrorToaster("Placez le curseur dans un tableau.");
      return;
    }

    const { table, selectedCell } = context;
    const referenceRow = selectedCell?.closest("tr") || table.rows[table.rows.length - 1];
    const rowCellCount = table.rows[0]?.cells?.length || 1;
    const newRow = document.createElement("tr");

    for (let i = 0; i < rowCellCount; i += 1) {
      const cell = document.createElement("td");
      cell.style.padding = "8px";
      cell.innerHTML = "&nbsp;";
      newRow.appendChild(cell);
    }

    referenceRow.insertAdjacentElement("afterend", newRow);
    syncEditorHtml(quillEditor);
  };

  const handleRemoveRow = () => {
    const quillEditor = getQuillEditor();
    if (!quillEditor) return;

    const context = getSelectedTableContext(quillEditor);
    if (!context || !context.selectedCell) {
      showErrorToaster("Placez le curseur sur la ligne a supprimer.");
      return;
    }

    const { table, selectedCell } = context;
    if (table.rows.length <= 1) {
      showErrorToaster("Le tableau doit contenir au moins une ligne.");
      return;
    }

    selectedCell.closest("tr")?.remove();
    syncEditorHtml(quillEditor);
  };

  const handleAddColumn = () => {
    const quillEditor = getQuillEditor();
    if (!quillEditor) return;

    const context = getSelectedTableContext(quillEditor);
    if (!context) {
      showErrorToaster("Placez le curseur dans un tableau.");
      return;
    }

    const { table, selectedCell } = context;
    const columnIndex = selectedCell ? selectedCell.cellIndex : (table.rows[0]?.cells.length || 1) - 1;

    Array.from(table.rows).forEach((row) => {
      const referenceCell =
        row.cells[columnIndex] || row.cells[row.cells.length - 1];
      const newCellTag = referenceCell?.tagName === "TH" ? "th" : "td";
      const newCell = document.createElement(newCellTag);
      newCell.style.padding = "8px";
      newCell.innerHTML = "&nbsp;";

      if (referenceCell) {
        referenceCell.insertAdjacentElement("afterend", newCell);
      } else {
        row.appendChild(newCell);
      }
    });

    syncEditorHtml(quillEditor);
  };

  const handleRemoveColumn = () => {
    const quillEditor = getQuillEditor();
    if (!quillEditor) return;

    const context = getSelectedTableContext(quillEditor);
    if (!context || !context.selectedCell) {
      showErrorToaster("Placez le curseur sur la colonne a supprimer.");
      return;
    }

    const { table, selectedCell } = context;
    const totalColumns = table.rows[0]?.cells?.length || 0;
    if (totalColumns <= 1) {
      showErrorToaster("Le tableau doit contenir au moins une colonne.");
      return;
    }

    const columnIndex = selectedCell.cellIndex;
    Array.from(table.rows).forEach((row) => {
      if (row.cells[columnIndex]) {
        row.deleteCell(columnIndex);
      }
    });

    syncEditorHtml(quillEditor);
  };

  const handleRemoveTable = () => {
    const quillEditor = getQuillEditor();
    if (!quillEditor) return;

    const context = getSelectedTableContext(quillEditor);
    if (!context) {
      showErrorToaster("Aucun tableau trouve.");
      return;
    }

    context.table.remove();
    syncEditorHtml(quillEditor);
  };

  const modules = {
    toolbar: {
      container: [
        [{ font: [] }],
        [{ header: [1, 2, 3, 4, 5, 6, false] }],
        ["bold", "italic", "underline", "strike"],
        [{ color: [] }, { background: [] }],
        [{ script: "sub" }, { script: "super" }],
        ["blockquote", "code-block"],
        [{ list: "ordered" }, { list: "bullet" }],
        ["link", "table"],
        ["clean"],
      ],
      handlers: {
        table: handleInsertTable,
      },
    },
  };

  const formats = [
    "font",
    "header",
    "bold",
    "italic",
    "underline",
    "strike",
    "color",
    "background",
    "script",
    "blockquote",
    "code-block",
    "list",
    "bullet",
    "link",
    "table",
  ];

  useEffect(() => {
    if (id) {
      const fetchRealisation = async () => {
        try {
          const response = await realisationService.getRealisationById(id);
          const data = response.data;

          // ✅ Formater la date pour l'input type="date"
          const formattedDate = data.date
            ? new Date(data.date).toISOString().split("T")[0]
            : "";

          setFormData({
            ...data,
            date: formattedDate,
          });

          setImagePreview(data.image || null);
        } catch (error) {
          showErrorToaster("Erreur lors du chargement des données.");
        }
      };
      fetchRealisation();
    }
  }, [id]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleDescriptionChange = (value) => {
    setFormData((prevData) => ({
      ...prevData,
      description: value,
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData((prevData) => ({
        ...prevData,
        image: file,
      }));
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    // Validation
    if (!formData.title || !formData.description || !formData.date) {
      setError("Veuillez remplir tous les champs obligatoires.");
      return;
    }

    try {
      const formDataToSend = new FormData();
      formDataToSend.append("title", formData.title);
      formDataToSend.append("description", formData.description);
      formDataToSend.append("date", formData.date);
      formDataToSend.append("slug", formData.slug);

      if (formData.image instanceof File) {
        formDataToSend.append("image", formData.image);
      }

      if (id) {
        await realisationService.updateRealisation(id, formDataToSend);
        showSuccessToaster("Modification réussie.");
      } else {
        await realisationService.createRealisation(formDataToSend);
        showSuccessToaster("Création réussie.");
      }

      navigate("/realisation-liste");
    } catch (err) {
      showErrorToaster(
        err.response
          ? err.response.data.message
          : "Une erreur est survenue, veuillez réessayer."
      );
    }
  };

  return (
    <div className="job-form-container">
      <h1>{id ? "Modifier réalisation" : "Créer réalisation"}</h1>
      <form onSubmit={handleSubmit}>
        {error && <div className="error-message">{error}</div>}

        <div className="form-group">
          <label style={{ color: "black" }}>Titre :</label>
          <input
            type="text"
            name="title"
            value={formData.title}
            onChange={handleChange}
          />
        </div>

        <div className="form-group">
          <label style={{ color: "black" }}>Description :</label>
          <div className="d-flex flex-wrap gap-2 mt-2 mb-3">
            <input
              type="number"
              min="1"
              value={tableRows}
              onChange={(e) => setTableRows(e.target.value)}
              placeholder="Lignes"
              style={{ maxWidth: "120px" }}
            />
            <input
              type="number"
              min="1"
              value={tableColumns}
              onChange={(e) => setTableColumns(e.target.value)}
              placeholder="Colonnes"
              style={{ maxWidth: "120px" }}
            />
            <button
              type="button"
              className="submit-button"
              onClick={handleInsertTable}>
              Inserer tableau
            </button>
            <button type="button" className="submit-button" onClick={handleAddRow}>
              + Ligne
            </button>
            <button type="button" className="submit-button" onClick={handleRemoveRow}>
              - Ligne
            </button>
            <button type="button" className="submit-button" onClick={handleAddColumn}>
              + Colonne
            </button>
            <button type="button" className="submit-button" onClick={handleRemoveColumn}>
              - Colonne
            </button>
            <button type="button" className="submit-button" onClick={handleRemoveTable}>
              Supprimer tableau
            </button>
          </div>
          <ReactQuill
            ref={quillRef}
            value={formData.description}
            onChange={handleDescriptionChange}
            theme="snow"
            placeholder="Écris ton texte ici..."
            modules={modules}
            formats={formats}
          />
        </div>

        <div className="form-group">
          <label style={{ color: "black" }}>Date :</label>
          <input
            type="date"
            name="date"
            value={formData.date}
            onChange={handleChange}
          />
        </div>

        <div className="form-group">
          <label style={{ color: "black" }}>Image :</label>
          <input
            type="file"
            name="image"
            accept="image/*"
            onChange={handleImageChange}
          />
          {imagePreview && <p>{imagePreview}</p>}
          <p>
            {formData.image instanceof File
              ? formData.image.name
              : formData.image
              ? "Image existante"
              : "Aucune image sélectionnée"}
          </p>
        </div>

        <button type="submit" className="submit-button">
          {id ? "Modifier" : "Créer"}
        </button>
      </form>
    </div>
  );
};

export default RealisationForm;
